import {Subscription} from 'rxjs/Subscription';
import {Injectable} from '@angular/core';
import {TakerService} from './taker.service';
import {StoreService} from './store.service';
import {AppStateService} from './appState.service';
import {PollUtilService} from './pollUtil.service';
import {AlertMessage, AlertService} from "../alert.service";
import {LoadingService} from "../loading.service";
import {WebUtilService} from "../webUtil.service";
import {LoggerService} from "../logger.service";
import {UtilService} from "../util.service";
import {
    DataRequestOptions,
    SubmittedResponses,
    TakerAppState,
    TakerResponseStatus,
    TakerValidationResponse
} from "../../models";

@Injectable()
export class WorkflowService {

    // extracted from URL (requested by taker)
    private _paramScriptId: string;
    private pollId: number;
    private anonymous: boolean;
    private wantResults: boolean;

    private disposable: Subscription;
    private repeatErrorCount: number = 0;

    constructor(
        private takerService: TakerService,
        private storeService: StoreService,
        private alertService: AlertService,
        private appStateService: AppStateService,
        private loadingService: LoadingService,
        private utilService: UtilService,
        private pollUtilService: PollUtilService,
        private webUtilService: WebUtilService,
        private logger: LoggerService) {
    }

    isAnonymous(): boolean {
        return this.anonymous;
    }


    getScriptId(): string {
        return this._paramScriptId;
    }

    init(pollAndScriptId: string, anonymous: boolean, wantResults: boolean) {

        this._paramScriptId = '0';  // default value
        this.pollId = null;
        this.anonymous = anonymous;
        this.wantResults = wantResults;

        let list = ((pollAndScriptId || '') + '').split(',');

        if (pollAndScriptId !== null && (list.length === 1 || list.length === 2)) {
            this.pollId = Number(list[list.length - 1]);
            if (list.length === 2) {
                this._paramScriptId = list[0];
            }
        } else {
            this.logger.log('WorkflowService: incorrect pollId param', pollAndScriptId);
            this.alertService.emit(new AlertMessage('Incorrect pollId', 'error'));

        }
        this.logger.log('WorkflowService: pollAndScriptId', pollAndScriptId,
            'list:', list, 'pollId:', this.pollId, 'anonymous:', this.anonymous, 'wantResults:', this.wantResults);

        this.setAndEmitState(new TakerAppState('poll'), null);

    }

    /**
     * Always call cancelOperation before this.takerService<FUN>
     * @param repeatOperation
     */
    cancelOperation(repeatOperation: boolean): boolean {

        this.logger.log('WorkflowService: cancelOperation', this.disposable,
            'repeatOperation:', repeatOperation, 'repeatErrorCount:', this.repeatErrorCount);

        this.loadingService.emit(false);

        // unsubscribe from the current HTTP request
        if (this.disposable && !this.disposable.closed) {
            this.disposable.unsubscribe();
            this.disposable = null;
        }

        // count repeats for repeatOperation==true operation (used to start taking poll when poll has changed)
        if (repeatOperation) {
            this.repeatErrorCount++;
        } else {
            this.repeatErrorCount = 0;
        }
        if (this.repeatErrorCount > 2) {
            this.alertService.emit(new AlertMessage('Too many attempts to get good response from server', 'error'));
        }
        return this.repeatErrorCount <= 2;
    }

    loadPoll(repeatOperation: boolean): void {

        const _self = this;
        this.logger.log('WorkflowService: loadPoll ENTER', this.pollId, 'repeatOperation:', repeatOperation,
            'repeatErrorCount:', this.repeatErrorCount);

        if (this.pollId) {
            if (!this.cancelOperation(repeatOperation)) {
                return;
            }
            this.disposable = this.takerService.getPoll(this.pollId, this._paramScriptId, this.getDataRequestOptions())
                .subscribe(
                    data => {
                        _self.logger.log('WorkflowService: loadPoll poll:', data);
                        this.handlePollSessionStateAfterGetPoll(data);
                    }
                );
        }
    }

    /**
     * check PollSessionState
     * CREATED      if(timeLimit) show_start_page else post.startPoll()
     * STARTED      post.getQuestions()
     * RESPONDED    post.getQuestions()
     * FINISHED     post.getResults()
     */
    handlePollSessionStateAfterGetPoll(data: any): void {

        let goodRespType = this.validateResponseType(data, true/*repeatableOperation*/);
        if (!goodRespType) {
            return;
        }

        // assign pollResponse to appState
        this.appStateService.setPollResponse(data);

        if (goodRespType && data.status) {
            this.logger.log('WorkflowService: handlePollSessionStateAfterGetPoll: data.status.state', data.status.state);

            if (this.wantResults) {
                // instead of validation just go to 'poll-results' view
                this.setAndEmitState(new TakerAppState('poll-results', {pollResponse: data}), data.status.token);
                // load poll results
                this.loadPollResults();
                return;
            }

            // checking poll anonymous flag
            switch (data.status.state) {
                case 'CREATED':
                    // if (data.record.timeLimit >= 0) {
                    this.setAndEmitState(new TakerAppState('start', {
                        pollResponse: data,
                        pollLocalStartTime: this.calcPollLocalStartTime(data.status)
                    }), data.status.token);
                    // } else {
                    //     this.startPoll();
                    // }
                    break;
                case 'STARTED':
                    this.loadQuestions(true/*repeatableOperation*/);
                    break;
                case 'RESPONDED':
                    this.loadQuestions(false/*repeatableOperation*/);
                    break;
                case 'FINISHED':
                    if (data.record.pollResponseEditableFlag || data.record.multipleResponsesFlag) {
                        this.setAndEmitState(new TakerAppState('start', {
                            pollResponse: data,
                            pollLocalStartTime: this.calcPollLocalStartTime(data.status)
                        }), data.status.token);
                        break;
                    }
                    this.setAndEmitState(new TakerAppState('taker-results', {
                        pollResponse: data,
                        pollLocalStartTime: this.calcPollLocalStartTime(data.status),
                        justSubmitted: false   // submitted earlier
                    }), data.status.token);
                    // this.setState(new AppStorageState('poll-results',
                    //     {pollResponse : data, pollLocalStartTime : this.calcPollLocalStartTime(data.status)}));
                    // this.loadPollResults();
                    break;
                default:
                    this.logger.error('WorkflowService: handlePollSessionStateAfterGetPoll: error data.status.state',
                        data.status.state, data);
                    break;
            }

        }
    }

    startPoll(): void {

        this.logger.log('WorkflowService: startPoll: ENTER', this.pollId, this._paramScriptId);

        if (!this.cancelOperation(false/*repeatOperation*/)) {
            return;
        }
        this.disposable = this.takerService.startPoll(this.pollId, this.getDataRequestOptions(), this._paramScriptId)
            .subscribe(
                data => {
                    this.logger.log('WorkflowService: startPoll:', data);

                    let goodRespType = this.validateResponseType(data, true/*repeatableOperation*/);
                    if (!goodRespType) {
                        return;
                    }

                    this.setAndEmitState(new TakerAppState('q-edit', {
                        pollResponse: this.appStateService.getLastAppState().context.pollResponse,
                        questionsResponse: data,
                        pollLocalStartTime: this.calcPollLocalStartTime(data.status)
                    }), data.status.token);
                }
            );
    }

    loadQuestions(repeatableOperation: boolean, /*Used for loading from file only*/questSetNum?: number): void {
        this.logger.log('WorkflowService: loadQuestions: ENTER', this.pollId, 'questSetNum:', questSetNum);

        if (!this.cancelOperation(false/*repeatOperation*/)) {
            return;
        }
        this.disposable = this.takerService.getQuestions(this.pollId,
            this.getDataRequestOptions(), this._paramScriptId, questSetNum)
            .subscribe(
                data => {
                    this.logger.log('WorkflowService: loadQuestions:', data);

                    let goodRespType = this.validateResponseType(data, repeatableOperation);
                    if (!goodRespType) {
                        return;
                    }

                    this.setAndEmitState(new TakerAppState('q-edit', {
                        pollResponse: this.appStateService.getLastAppState().context.pollResponse,
                        questionsResponse: data,
                        pollLocalStartTime: this.calcPollLocalStartTime(data.status)
                    }), data.status.token);
                }
            );
    }

    resubmit(questSetNum: number, submitAndNext: boolean) {

        this.logger.log('WorkflowService: resubmit: ENTER',
            this.pollId, this._paramScriptId, questSetNum, submitAndNext, this.storeService.getSubmittedResponses().obj);

        this.submit(this.storeService.getSubmittedResponses().obj, questSetNum, submitAndNext);
    }

    submit(obj: any, questSetNum: number, submitAndNext: boolean) {

        this.logger.log('WorkflowService: submit: ENTER', this.pollId, this._paramScriptId, questSetNum, submitAndNext, obj);

        this.storeService.setSubmittedResponses(new SubmittedResponses(obj));

        if (!this.cancelOperation(false/*repeatOperation*/)) {
            return;
        }

        this.disposable = this.takerService.submit(obj, this.pollId,
            this.getDataRequestOptions(), this._paramScriptId, questSetNum, submitAndNext)
            .subscribe(
                data => {
                    this.logger.log('WorkflowService: submit:', data);
                    this.handleSubmitResponse(data, submitAndNext);
                }
            );
    }

    backToEdit(validationResponse: TakerValidationResponse): void {

        this.logger.log('WorkflowService: validationResponse:', validationResponse);

        let questionsResponse: any = this.appStateService.getLastAppState().context.questionsResponse;
        // injecting records to questionsResponse
        questionsResponse['records'] = validationResponse['records'];

        this.setAndEmitState(new TakerAppState('q-edit', {
            pollResponse: this.appStateService.getLastAppState().context.pollResponse,
            questionsResponse: questionsResponse,
            // take last pollLocalStartTime when going Back as we did not get new
            // status from back-end
            pollLocalStartTime: this.appStateService.getPollLocalStartTime()
        }), validationResponse.status.token);
    }

    handleSubmitResponse(data: any, submitAndNext: boolean): void {

        let goodRespType = this.validateResponseType(data, false/*repeatableOperation*/);

        if (!goodRespType) {
            return;
        }

        const pollLocalStartTime: number = this.calcPollLocalStartTime(data.status);

        if (goodRespType && data.responseType) {
            this.logger.log('WorkflowService: handleSubmitResponse: data.responseType', data.responseType);
            switch (data.responseType) {
                case 'VALIDATED':
                    // stay editing (show errors)
                    this.setAndEmitState(new TakerAppState('q-view', {
                        pollResponse: this.appStateService.getLastAppState().context.pollResponse,
                        validationResponse: data,
                        questionsResponse: this.appStateService.getLastAppState().context.questionsResponse,
                        pollLocalStartTime: pollLocalStartTime
                    }), data.status.token);
                    break;
                case 'QUESTIONS':
                    // display next set of questions
                    // this.storeService.questionsResponse = data;
                    this.setAndEmitState(new TakerAppState('q-edit', {
                        pollResponse: this.appStateService.getLastAppState().context.pollResponse,
                        questionsResponse: data,
                        pollLocalStartTime: pollLocalStartTime
                    }), data.status.token);
                    break;
                case 'COMPLETED':
                    // this.storeService.questionsResponse = null;
                    // poll is completed,  its new status (FINISHED) is in data
                    // display taker results now
                    this.setAndEmitState(new TakerAppState('taker-results', {
                        pollResponse: this.appStateService.getLastAppState().context.pollResponse,
                        takerResultsResponse: data,
                        pollLocalStartTime: pollLocalStartTime,
                        justSubmitted: true
                    }), data.status.token);
                    break;
            }

        }
    }

    loadPollResults() {

        this.logger.log('WorkflowService: loadPollResults: ENTER', this.pollId);

        if (!this.cancelOperation(false/*repeatOperation*/)) {
            return;
        }

        // check if results are available first
        const pollResponse = this.appStateService.getLastAppState().context.pollResponse;
        const resultsAvailability = this.pollUtilService.getResultsAvailability(
            pollResponse.record, pollResponse.status ? pollResponse.status.state : null
        );

        if (!resultsAvailability.availableNow || !resultsAvailability.takerCompleted) {
            this.logger.log('WorkflowService:loadPollResults:', 'resultsAvailability:', resultsAvailability);
            this.setAndEmitState(new TakerAppState('poll-results', {
                pollResponse: this.appStateService.getLastAppState().context.pollResponse,
                pollResultsResponse: null
            }), this.appStateService.getLastAppState().context.pollResponse.status.token);
            return;
        }

        this.disposable = this.takerService.getPollResults(this.pollId, this.getDataRequestOptions())
            .subscribe(
                data => {
                    this.logger.log('WorkflowService:loadPollResults:', data);

                    let goodRespType = this.validateResponseType(data, true/*repeatableOperation*/);
                    if (!goodRespType) {
                        return;
                    }

                    this.setAndEmitState(new TakerAppState('poll-results', {
                        pollResponse: this.appStateService.getLastAppState().context.pollResponse,
                        pollResultsResponse: data
                    }), data.status.token);
                }
            );

    }

    validateResponseType(data: any, repeatableOperation: boolean): boolean {

        let goodRespType = false;

        if (data.responseType) {

            this.logger.log('WorkflowService: validateResponseType: data.responseType', data.responseType,
                'repeatableOperation:', repeatableOperation);

            switch (data.responseType) {

                case 'ERROR':
                    this.alertService.emit(new AlertMessage(data.error, 'error'));
                    break;

                case 'POLL_CHANGED':
                    if (repeatableOperation) {
                        this.loadPoll(true/*repeatOperation*/);
                    } else {
                        this.alertService.emit(new AlertMessage(data.error, 'pollChanged'));
                    }
                    break;

                case 'POLL':
                case 'VALIDATED':
                case 'QUESTIONS':
                case 'COMPLETED':
                case 'RESULTS':
                    goodRespType = true;
                    break;

                default:
                    this.logger.error('WorkflowService: validateResponseType: error data.responseType', data.responseType, data);
                    break;
            }
        }
        return goodRespType;
    }

    private setAndEmitState(newState: TakerAppState, token: string): void {

        // scroll page to top
        this.webUtilService.scrollTop();

        newState.context.scriptId = this._paramScriptId;
        newState.context.pollId = this.pollId;
        newState.context.anonymous = this.anonymous;
        newState.context.token = token;

        this.logger.log('WorkflowService: setState(final):', newState);

        this.appStateService.emit(newState);

    }

    /**
     * Calculated poll start time based on browser local time
     * @param status
     * @returns {any}
     */
    private calcPollLocalStartTime(status: TakerResponseStatus): number {

        let localStartTime: number = null;
        let serverTimeUsed: number = null;

        if (status.currentTime && status.startTime) {
            const now = new Date().getTime();
            serverTimeUsed = status.currentTime - status.startTime;
            localStartTime = now - serverTimeUsed;
        }

        return localStartTime;

    }

    private getDataRequestOptions(): DataRequestOptions {

        const isSignedUser = this.pollUtilService.isSignedUser(this.appStateService.getConfig());
        const token = isSignedUser ? null : this.appStateService.getLastAppState().context.token;
        const options: DataRequestOptions = new DataRequestOptions(this.anonymous, token);

        return options;
    }

}
